import { sortedClips, Track } from './timeline.js';
import { snapToFrame } from './util/time.js';
/** Der Clip, der den Abspielkopf gerade überdeckt. */
export function clipBei(track, zeit) {
    return sortedClips(track).find((c) => zeit >= c.start - 1e-6 && zeit < c.start + c.duration - 1e-6) ?? null;
}
/**
 * Die drei Stellen, ausgerechnet.
 *
 * „Davor“ und „danach“ beziehen sich auf den Clip unter dem Abspielkopf.
 * Steht der Kopf im Freien, ist der Bezug der nächste Clip in die jeweilige
 * Richtung — sonst hiesse „danach“ auf einer leeren Stelle gar nichts.
 */
export function stelleFuer(track, kopf, art, dauer, fps) {
    const raster = (t) => Math.max(0, snapToFrame(t, fps));
    if (art === 'hier')
        return { start: raster(kopf) };
    const clips = sortedClips(track);
    if (clips.length === 0) {
        return { start: null, grund: 'Auf dieser Ebene liegt noch nichts — „genau hier“ ist die einzige Stelle.' };
    }
    const drunter = clipBei(track, kopf);
    if (art === 'davor') {
        const bezug = drunter ?? clips.find((c) => c.start >= kopf);
        if (!bezug)
            return { start: null, grund: 'Vor dem Abspielkopf liegt auf dieser Ebene nichts mehr.' };
        // Bündig davor: es endet genau da, wo der Bezug anfängt. Passt es davor
        // nicht mehr hin, fängt es bei null an -- und der Konflikt sagt es dann.
        return { start: raster(Math.max(0, bezug.start - dauer)), bezug };
    }
    const bezug = drunter ?? [...clips].reverse().find((c) => c.start + c.duration <= kopf);
    if (!bezug)
        return { start: null, grund: 'Nach dem Abspielkopf liegt auf dieser Ebene nichts.' };
    return { start: raster(bezug.start + bezug.duration), bezug };
}
/**
 * **Welche Stellen das Einfügen-Blatt anbietet** (7.153).
 *
 * Sascha, 23. September, mit Bildschirmfoto: „Das einfüge Menü ist voll
 * unverständlich“. Ein Bild sollte auf eine leere Ebene, und das Blatt zeigte
 * „Davor“ und „Danach“ grau -- warum, sagte ein Tooltip, und den gibt es auf
 * dem Telefon nicht. Jetzt steht nur da, was geht:
 *
 *  - „am Abspielkopf“ immer, auch wenn dort etwas liegt -- dann sagt das
 *    Blatt, wohin stattdessen;
 *  - „davor“ und „danach“ nur, wenn es sie auf dieser Ebene gibt *und* es
 *    dort ohne Zusammenstoss hinpasst. Ihr ganzer Sinn ist „bündig, ohne
 *    etwas zu verschieben“; eines, das sofort in einen Konflikt führt, ist
 *    kein Angebot.
 */
export function moeglicheStellen(track, kopf, dauer, fps) {
    return ['hier', 'davor', 'danach'].filter((art) => {
        const start = stelleFuer(track, kopf, art, dauer, fps).start;
        if (start === null)
            return false;
        return art === 'hier' || konfliktBei(track, start, dauer) === null;
    });
}
/** Was an dieser Stelle im Weg liegt -- oder null. */
export function konfliktBei(track, start, dauer, ausser) {
    return (sortedClips(track).find((c) => c.id !== ausser && start < c.start + c.duration - 1e-6 && start + dauer > c.start + 1e-6) ??
        null);
}
/**
 * Wo eine neue Spur hinkommt, wenn man dem Konflikt ausweicht.
 *
 * Die Reihenfolge im Modell ist die Reihenfolge im Bild: die erste Bildspur
 * ist die Grundspur, alles Spätere liegt darüber. Die Zeitleiste zeigt sie
 * umgedreht, damit oben auch oben heisst.
 *
 * Unter die Grundspur geht nichts: sie gibt Bild und Takt vor, und eine neue
 * Spur an ihrer Stelle würde beides übernehmen. Das wäre keine Ausweichspur
 * mehr, sondern ein anderer Schnitt.
 */
export function spurIndexFuer(tracks, trackId, ausweg) {
    const index = tracks.findIndex((t) => t.id === trackId);
    if (index < 0)
        return null;
    if (ausweg === 'darueber')
        return index + 1;
    const spur = tracks[index];
    if (spur.kind === 'video' && tracks.findIndex((t) => t.kind === 'video') === index)
        return null;
    return index;
}
/** Ob „darunter“ überhaupt angeboten werden darf, und warum nicht. */
export function darunterMoeglich(tracks, trackId) {
    if (spurIndexFuer(tracks, trackId, 'darunter') === null) {
        return { ok: false, grund: 'Unter der Grundspur geht nichts — sie gibt Bild und Takt vor.' };
    }
    return { ok: true };
}
/**
 * **Wo Medien aus der Mediathek hinkommen, ohne dass jemand gefragt wird**
 * (7.156).
 *
 * Sascha, 24. September: „Wenn ich + drücke, fügt es nicht direkt ein nach
 * dem Auswählen in der Mediathek. Und auch wenn ich mehrere auswähle.“ Was
 * über das Plus vom Gerät kam, landete in der Medienliste und nicht im
 * Schnitt -- jede Datei musste danach noch einmal angetippt werden.
 *
 * Jetzt kommt sie **an den Marker**, so wie jedes Schnittprogramm auf dem
 * Telefon es tut. Steht der Marker mitten in einem Clip, kommt sie *hinter*
 * diesen Clip: zerschnitten wird dabei nichts. Steht er am Anfang eines
 * Clips oder in einer Lücke, genau dorthin.
 */
export function einfuegePunkt(track, kopf, fps) {
    const k = Math.max(0, snapToFrame(kopf, fps));
    const drunter = clipBei(track, k);
    if (drunter && k > drunter.start + 1e-6)
        return drunter.start + drunter.duration;
    return k;
}
/**
 * **Genau am Marker -- auch mitten in einem Clip** (7.197).
 *
 * Sascha, 4. Oktober: „das + ist irgendwie auch doof so. Man kann halt nicht
 * an der Stelle einfügen“. `einfuegePunkt` legte das Neue *hinter* den Clip
 * unter dem Marker, damit nichts zerschnitten wird -- nur steht es dann eben
 * nicht dort, wo man hingetippt hat. Jetzt wird der Clip am Marker geteilt,
 * und das Neue kommt dazwischen; der Rest rückt nach.
 *
 * Steht der Marker höchstens ein Bild neben einer Kante, gilt die Kante:
 * ein Stück von einem Bild ist kein Stück. Zurück kommt die Stelle und, wenn
 * geteilt werden muss, der Clip und wie weit in ihm.
 */
export function einfuegenAmKopf(track, kopf, fps) {
    const k = Math.max(0, snapToFrame(kopf, fps));
    const drunter = clipBei(track, k);
    if (!drunter)
        return { punkt: k };
    const bild = 1 / fps;
    // Wie beim Teilen (`splitAt`): jede Hälfte behält mehr als ein Bild.
    if (k <= drunter.start + bild + 1e-6)
        return { punkt: drunter.start };
    if (k >= drunter.start + drunter.duration - bild - 1e-6)
        return { punkt: drunter.start + drunter.duration };
    return { punkt: k, teilen: { clip: drunter, lokal: k - drunter.start } };
}
/**
 * Legt eine Reihe von Dauern hintereinander an `punkt` und rechnet aus, wie
 * weit alles dahinter nachrücken muss.
 *
 * Nachgerückt wird nur so weit wie nötig: passt die Reihe in eine Lücke,
 * bleibt der Rest stehen. Und ein Clip, der mit seiner Blende in den
 * vorigen hineinreicht (7.89: der Übergang *ist* die Überlappung), gilt als
 * dahinter -- er behält seine Blende, jetzt gegen den letzten neuen Clip.
 */
export function imFilmEinfuegen(track, punkt, dauern) {
    const starts = [];
    let t = punkt;
    for (const d of dauern) {
        starts.push(t);
        t += d;
    }
    const ende = t;
    const folgende = sortedClips(track).filter((c) => c.start + c.duration > punkt + 1e-6 && c.start >= punkt - (c.transitionIn?.duration ?? 0) - 1e-6);
    const erster = folgende[0];
    if (!erster)
        return { starts, verschoben: [], ende };
    const ueberlappung = Math.max(0, punkt - erster.start);
    const um = Math.max(0, ende - ueberlappung - erster.start);
    if (um <= 1e-9)
        return { starts, verschoben: [], ende };
    return { starts, verschoben: folgende.map((c) => ({ clipId: c.id, start: c.start + um })), ende };
}
export function zwischenraeume(track, fps) {
    const bild = 1 / Math.max(1, fps);
    const clips = sortedClips(track);
    const raus = [];
    if (clips[0] && clips[0].start > bild + 1e-6)
        raus.push({ art: 'luecke', von: 0, bis: clips[0].start });
    for (let i = 1; i < clips.length; i++) {
        const vor = clips[i - 1];
        const dieser = clips[i];
        const ende = vor.start + vor.duration;
        if (dieser.start > ende + bild + 1e-6)
            raus.push({ art: 'luecke', von: ende, bis: dieser.start });
        else if (dieser.start + dieser.duration > ende + 1e-6)
            raus.push({ art: 'schnitt', bei: dieser.start, clipId: dieser.id, vorClipId: vor.id });
    }
    return raus;
}
/**
 * **Vor und hinter den Clips einer Spur** (7.233; Sascha, 6. Oktober: auf
 * Video 1 „eher zwischen den Clips oder am Anfang und Ende eines Clips“, auf
 * einer belegten Ebene „vorne und hinter einem Clip sowie dann natürlich
 * dazwischen“). Dazwischen sind die Schnitte und Lücken aus
 * `zwischenraeume`; hier kommt der Rand dazu:
 *
 *   - `anfang`, wenn der erste Clip bei 0 beginnt -- davor ist keine Lücke,
 *     also auch kein Plus darin;
 *   - `ende` hinter dem letzten Clip.
 *
 * Mitten in einen Clip gibt es kein Plus: das teilte ihn.
 */
export function randStellen(track, fps) {
    const bild = 1 / Math.max(1, fps);
    const clips = sortedClips(track);
    const erster = clips[0];
    if (!erster)
        return [];
    const raus = [];
    if (erster.start <= bild + 1e-6)
        raus.push({ art: 'anfang', bei: 0, clipId: erster.id });
    let letzter = erster;
    for (const c of clips)
        if (c.start + c.duration > letzter.start + letzter.duration)
            letzter = c;
    raus.push({ art: 'ende', bei: letzter.start + letzter.duration, clipId: letzter.id });
    return raus;
}
/**
 * Eine Lücke schliessen: alles ab ihrem Ende rückt um ihre Länge nach vorn,
 * auf dieser Spur und nur dort (7.215). Von vorn nach hinten, damit nie ein
 * Clip auf dem landet, der gleich weicht.
 */
export function lueckeZu(track, luecke) {
    const um = luecke.bis - luecke.von;
    if (!(um > 0))
        return [];
    return sortedClips(track)
        .filter((c) => c.start >= luecke.bis - 1e-6)
        .map((c) => ({ clipId: c.id, start: Math.max(0, c.start - um) }));
}
/**
 * **Wohin Text und Vorlagen aus dem Markenset kommen** (Sascha, 12. Oktober:
 * „Marken Sets lassen sich nicht einfügen“).
 *
 * Bis dahin kamen sie immer auf die zweite Bildspur, ob dort etwas lag oder
 * nicht. Lag dort am Kopf ein Video, landete die Bauchbinde *auf demselben
 * Platz* wie das Video -- im Schnitt war sie da, zu sehen war sie nicht.
 *
 * Eine Grafik gehört nach oben: auf die oberste Bildspur über der Grundspur,
 * wenn dort von `start` bis `start + dauer` Platz ist und die Spur nicht
 * gesperrt ist. Sonst kommt eine neue Bildspur darüber -- wie beim Einfügen
 * von Medien mit „Ebene darüber“. Auf die Grundspur kommt nie eine Grafik:
 * sie gibt Bild und Takt vor.
 */
export function grafikEbene(tracks, start, dauer) {
    const bild = tracks.map((track, index) => ({ track, index })).filter((s) => s.track.kind === 'video');
    const oberste = bild.length > 1 ? bild[bild.length - 1] : null;
    if (oberste && !oberste.track.locked && konfliktBei(oberste.track, start, dauer) === null) {
        return { trackId: oberste.track.id, neueSpur: null };
    }
    let n = 1;
    while (tracks.some((t) => t.id === `V${n}`))
        n++;
    const track = Track.parse({ id: `V${n}`, kind: 'video' });
    return { trackId: track.id, neueSpur: { track, index: (bild[bild.length - 1]?.index ?? -1) + 1 } };
}
//# sourceMappingURL=einfuegen.js.map